import { centralEnabled, centralAccessToken, rejectCentralSession, centralUser } from './centralAuth';
import axios, { AxiosResponse, AxiosError, AxiosHeaders } from "axios";
import { TokenService } from "./TokenService";
import { appEnv } from "../config/env";
import { actionEmitter, EmitEvent } from "../config/ActionEmitter";
import { IApiResponse } from "../models/Interfaces";

const ACCOUNT_STATUS_MESSAGE_KEY = "account_status_message";

export class AjaxService {
  private tokenService: TokenService;

  constructor() {
    this.tokenService = new TokenService();
  }

  // Generic GET method
  public async Get<T>(
    url: string,
    auth: boolean = false,
    successfunc?: (data: T) => void,
    errorfunc?: (success: boolean, message: string) => void,
    silent: boolean = false,
  ): Promise<T | null> {
    const geturl = (appEnv.apiUrl ?? "") + url;
    if (auth && centralEnabled) await centralAccessToken();
    const headers = this.GetHeaders(auth);

    try {
      const result = await axios.get<T>(geturl, { headers });

      if (result.status >= 200 && result.status <= 299 && result.data) {
        successfunc?.(result.data);
        return result.data;
      }

      const errorMessage = result.data ? "An application error occurred." : "No data received.";
      errorfunc?.(false, errorMessage);
      return null;
    } catch (error: any) {
      const apiResponse = this.handleApiError(error, !silent);
      const message = apiResponse?.Messages?.[0] ?? `Unable to get data, network issue occurred: ${error.message}`;
      errorfunc?.(false, message);
      throw apiResponse ?? error;
    }
  }

  // Generic POST method
  public async Post<T>(
    url: string,
    command: any,
    auth: boolean = false,
    successfunc?: (data: T) => void,
    errorfunc?: (success: boolean, message: string) => void,
  ): Promise<T | null> {
    const posturl = (appEnv.apiUrl ?? "") + url;
    const postmodel = JSON.stringify(command);
    if (auth && centralEnabled) await centralAccessToken();
    const headers = this.GetHeaders(auth);

    try {
      const result = await axios.post<T>(posturl, postmodel, { headers });

      if (result.status >= 200 && result.status <= 299 && result.data) {
        successfunc?.(result.data);
        return result.data;
      }

      const errorMessage = result.data ? "An application error occurred." : "No data received.";
      errorfunc?.(false, errorMessage);
      return null;
    } catch (error: any) {
      const apiResponse = this.handleApiError(error);
      const message = apiResponse?.Messages?.[0] ?? `Unable to send data, network issue occurred: ${error.message}`;
      errorfunc?.(false, message);
      throw apiResponse ?? error;
    }
  }

  public async PostFormData<T>(
    url: string,
    formData: FormData,
    auth: boolean = false,
    successfunc?: (data: T) => void,
    errorfunc?: (success: boolean, message: string) => void,
  ): Promise<T | null> {
    const posturl = (appEnv.apiUrl ?? "") + url;
    if (auth && centralEnabled) await centralAccessToken();
    const headers = this.GetHeaders(auth);

    headers.delete("Content-Type");

    try {
      const result = await axios.post<T>(posturl, formData, { headers });

      if (result.status >= 200 && result.status <= 299 && result.data) {
        successfunc?.(result.data);
        return result.data;
      }

      const errorMessage = result.data ? "An application error occurred." : "No data received.";
      errorfunc?.(false, errorMessage);
      return null;
    } catch (error: any) {
      const apiResponse = this.handleApiError(error);
      const message = apiResponse?.Messages?.[0] ?? `Unable to send data, network issue occurred: ${error.message}`;
      errorfunc?.(false, message);
      throw apiResponse ?? error;
    }
  }

  // Helper to get headers, with optional Authorization header
  public GetHeaders(auth: boolean): AxiosHeaders {
    const selectedsiteid = localStorage.getItem(appEnv.storageCurrentSiteKey);
    const headers: AxiosHeaders = new AxiosHeaders();

    headers.set("Content-Type", "application/json");
    headers.set("SELECTED-SITEID", selectedsiteid || "");

    if (auth) {
      const token = this.tokenService.getToken();

      if (token) {
        headers.Authorization = `${token.access_token}`;
      }
    }

    return headers;
  };

  public async GetBlob(url: string, auth: boolean = false): Promise<Blob> {
    const geturl = (appEnv.apiUrl ?? "") + url;
    if (auth && centralEnabled) await centralAccessToken();
    const headers = this.GetHeaders(auth);
    headers.delete("Content-Type");

    try {
      const result = await axios.get<Blob>(geturl, { headers, responseType: "blob" });
      const headerType = String(result.headers["content-type"] ?? "").split(";")[0].trim();
      if (!result.data || headerType.includes("json") || headerType.startsWith("text/")) {
        throw new Error("The file could not be downloaded.");
      }

      if (headerType && result.data.type !== headerType) {
        return new Blob([result.data], { type: headerType });
      }

      return result.data;
    } catch (error: any) {
      if (!error?.response) {
        throw error instanceof Error ? error : new Error("The file could not be downloaded.");
      }

      throw new Error(await this.readBlobError(error));
    }
  }

  private async readBlobError(error: any): Promise<string> {
    const data = error?.response?.data;
    if (data instanceof Blob) {
      try {
        const parsed = JSON.parse(await data.text());
        const message = parsed?.Messages?.[0];
        if (typeof message === "string" && message.trim()) {
          return message;
        }
      } catch {
        // The error body was not JSON.
      }
    }

    return "The file could not be downloaded.";
  }

  // Optional helper for specific Authorization-only header
  public GetHeaderBearerOnly(): string {
    const token = this.tokenService.getToken();

    if (token) {
      return `${token.access_token}`;
    } else {
      return "";
    }
  };

  public GetExternal<T>(url: string, headers?: Record<string, string>): Promise<any> {
    return new Promise((resolve, reject) => {
      axios
        .get(url, { headers: headers })
        .then((result: AxiosResponse<T>) => {
          if (result.status >= 200 && result.status <= 299 && result.data) {
            resolve(result.data);
          } else {
            const errorMessage = result.data ? "An application error occurred." : "No data received.";
            resolve(null);
          }
        })
        .catch((error: AxiosError) => {
          const message = `Unable to get data, network issue occurred: ${error.message}`;
          reject(message);
        });
    });
  };

  public PostExternal<T>(url: string, command: any): Promise<T | null> {
    return new Promise((resolve, reject) => {
      const postmodel = JSON.stringify(command);
      const headers = this.GetHeaders(false);

      axios
        .post<T>(url, postmodel, { headers })
        .then((result: AxiosResponse<T>) => {
          if (result.status >= 200 && result.status <= 299 && result.data) {
            resolve(result.data);
          } else {
            const errorMessage = result.data ? "An application error occurred." : "No data received.";
            resolve(null);
          }
        })
        .catch((error: AxiosError) => {
          const message = `Unable to send data, network issue occurred: ${error.message}`;
          reject(message);
        });
    });
  };

  private handleApiError(error: any, notify: boolean = true): IApiResponse | null {
    if (centralEnabled && error?.response?.status === 401) {
      const token = centralUser()?.access_token;
      if (token) void rejectCentralSession(token);
    }
    const apiResponse = error?.response?.data as IApiResponse | undefined;

    if (!apiResponse) {
      return null;
    }

    const message = apiResponse.Messages?.[0] ?? "An application error occurred.";
    if (notify) actionEmitter.emit(EmitEvent.Result, false, message);

    if (typeof window !== "undefined" && apiResponse.LastActionName) {
      sessionStorage.setItem(ACCOUNT_STATUS_MESSAGE_KEY, message);

      if (apiResponse.LastActionName === "accountActionRequired" && window.location.pathname !== "/account/details") {
        window.dispatchEvent(new Event("cms-access-changed"));
      }
    }

    return apiResponse;
  }
}
